/**
 * Next.js client-side instrumentation. Runs once at startup before any
 * user code. Wires up Sentry SDK -> GlitchReplay ingest, plus the
 * @glitchreplay/a11y axe-core scanner on dev / staging.
 *
 * Replaces the prior in-house components/GlobalErrorHandler.tsx, which
 * is left in place for the 14-day overlap window per the GlitchReplay
 * migration plan.
 */
import * as Sentry from "@sentry/react";
import { axeIntegration } from "@glitchreplay/a11y";
import { networkProbeIntegration } from "@glitchreplay/network-probe";
import { cspIntegration } from "./lib/glitchreplay-csp";
import {
  GLITCHREPLAY_HOST,
  GLITCHREPLAY_PROJECT_ID,
  GLITCHREPLAY_PUBLIC_KEY,
} from "./lib/glitchreplay-dsn";

// Project/key resolved from NEXT_PUBLIC_GLITCHREPLAY_DSN — see lib/glitchreplay-dsn.ts.
const PROJECT_ID = GLITCHREPLAY_PROJECT_ID;
const PUBLIC_KEY = GLITCHREPLAY_PUBLIC_KEY;
// The Sentry SDK DSN carries project id `0` — GlitchReplay maps the public
// key to the real project server-side; the REST calls below use PROJECT_ID.
const DSN = `https://${PUBLIC_KEY}@${GLITCHREPLAY_HOST}/0`;

// Replay privacy/redaction flags. Static defaults match the dashboard's
// "balanced" level (Settings → Replays → Privacy level on glitchreplay.com).
// Subsequent page loads use whatever the dashboard slider was last set to
// — cached from the previous /config fetch below. rrweb masks at snapshot
// time, so a change in the dashboard takes effect at the *next* page load,
// not mid-session.
const PRIVACY_CACHE_KEY = `gr:privacy:${PROJECT_ID}`;
type PrivacyFlags = {
  maskAllText: boolean;
  maskAllInputs: boolean;
  blockAllMedia: boolean;
};
const DEFAULT_PRIVACY: PrivacyFlags = {
  maskAllText: false,
  maskAllInputs: true,
  blockAllMedia: true,
};
function readCachedPrivacy(): PrivacyFlags {
  if (typeof localStorage === "undefined") return DEFAULT_PRIVACY;
  try {
    const raw = localStorage.getItem(PRIVACY_CACHE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PrivacyFlags>;
      return {
        maskAllText:
          typeof p.maskAllText === "boolean" ? p.maskAllText : DEFAULT_PRIVACY.maskAllText,
        maskAllInputs:
          typeof p.maskAllInputs === "boolean" ? p.maskAllInputs : DEFAULT_PRIVACY.maskAllInputs,
        blockAllMedia:
          typeof p.blockAllMedia === "boolean" ? p.blockAllMedia : DEFAULT_PRIVACY.blockAllMedia,
      };
    }
  } catch {}
  return DEFAULT_PRIVACY;
}
const PRIVACY = readCachedPrivacy();

const NOISE_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  // Browser-extension content scripts that double-inject on SPA route changes.
  // We do not declare ScreenshotConfig anywhere in our bundle.
  "Identifier 'ScreenshotConfig' has already been declared",
  // Wallet-extension provider rejections — never reachable from our app.
  "Failed to connect to MetaMask",
  // History API protected by an extension wrapper; we never reassign pushState.
  "Cannot assign to read only property 'pushState' of object",
  // Minified React #310 ("Rendered more hooks than during the previous
  // render") with no in-app stack frames — extension-injected DOM mutating
  // between renders. Filtered when stack-less; real cases will retain a
  // stack and be kept by the existing beforeSend logic.
  "Minified React error #310",
  // Cloudflare Zaraz worker-port teardown: when the Zaraz worker's
  // session-keyed port ("content {uuid}") is closed mid-flight (navigation
  // or worker restart), Zaraz throws this from the main thread. The stack
  // frame URL is the page itself, not a zaraz script, so denyUrls misses it.
  "Port 'content ",
  // iOS WebView/native shell bridge noise. We never define or call this
  // handler; it is injected by the host browser/app and can be absent.
  "top.webkit.messageHandlers.foregroundToBackground.postMessage",
];

// Stack-less or third-party-stack rejection messages thrown from embedded
// widgets / browser quirks rather than our code. Drop when there's no
// usable stack — same logic as the existing "Failed to fetch" filter.
const NOISE_REJECTION_MESSAGES_NO_STACK = [
  "Failed to fetch",
  // Firefox transient network error during cancelled fetch / offline.
  "NetworkError when attempting to fetch resource.",
  // Safari transient network error.
  "Load failed",
  // Apollo / HubSpot / Twilio Flex chat fetch wrappers throw these on
  // upstream blips; without a stack pointing into our code there's
  // nothing to do on our side.
  /^Response not (ok|successful)/i,
  // PromiseRejectionEvent with no reason at all.
  "Unhandled promise rejection",
  // Sentry's synthetic message when a Promise rejects with a non-Error
  // value (plain object, primitive, etc). Without a stack there's no way
  // to attribute it to our code — typically extension content scripts or
  // 3rd-party widgets rejecting with raw error-shaped objects like
  // {code, message}. Counter-evidence: gr_5avcdmc (ascii-art-generator).
  /^Non-Error promise rejection captured/i,
  // Safari's SyntaxError text for invalid URL/regex parse — thrown by
  // `new URL("...")`, `decodeURIComponent("%")`, PaymentRequest, WebAuthn
  // and a handful of Safari-internal APIs. With no stack it's invariably
  // an extension or Safari API call we don't own. Chrome's equivalent
  // ("Invalid URL") never makes it through stack-less and the same code
  // path fingerprints differently there.
  // Counter-evidence: gr_7b9x43k (nist-800-88, Safari 26.5).
  "The string did not match the expected pattern.",
];

// Third-party tracker / ad / analytics scripts. Errors thrown from frames
// inside these URLs are noise — filter them at the SDK boundary so they
// never hit the wire. Mirrors the legacy GlobalErrorHandler `noisePatterns`
// list, expanded with Zaraz / Cloudflare Insights observed in the wild.
const NOISE_DENY_URLS = [
  /zaraz/i,
  /cloudflareinsights/i,
  /connect\.facebook\.net/i,
  /fbevents/i,
  /googletagmanager/i,
  /googlesyndication/i,
  /fundingchoicesmessages/i,
  /google-analytics/i,
  /clarity\.ms/i,
  /js\.hs-(scripts|banner|analytics|adspixel)/i,
  /js\.hscollectedforms/i,
  /js\.hubspot/i,
  /js\.usemessages/i,
  /analytics\.ahrefs/i,
  /fonts\.googleapis\.com\/css\?family=Roboto(?:%3A|:)500/i,
  /fonts\.googleapis\.com\/css\?family=Google(?:%20|\+)Sans/i,
  // Embedded Google APIs (gapi loader, Translate widget, gstatic asset host).
  // These don't originate from our pages — usually injected by Translate
  // browser extensions or scraper previews.
  /apis\.google\.com/i,
  /translate\.googleapis\.com/i,
  /gstatic\.com\/.*\/translate/i,
  // Public CDN runtimes pulled in by user-injected snippets / sandboxes
  // (jsdelivr Recharts/jsPDF/prop-types failures appear when an embedder
  // rate-limits or strips the script).
  /cdn\.jsdelivr\.net/i,
  // Browser-extension trackers that piggyback on pages.
  /gtmpx\.com/i,
  /scriptcdn\.net/i,
  /amazingrefresh\.com/i,
  // Third-party icon CDN — replaced with inline SVG / local assets where
  // we used it; remaining refs come from cached pages or scraped previews.
  /img\.icons8\.com/i,
  // Cloudflare Zaraz proxies GA through the site's own domain at /ga/<token>.
  // Network-layer ad-blockers / DNS filters catch even same-domain proxies,
  // producing resource_error noise identical to a direct third-party block.
  /inventivehq\.com\/ga\//i,
];

// Crypto-wallet browser extensions (Binance Wallet, etc.) inject an in-page
// provider into the document's main world, so the CSP violation they trip
// shows the page — not a chrome-extension:// URL — as its sourceFile and
// slips past cspIntegration's scheme filter. Key off the blocked host
// instead. Same class of noise as the "Failed to connect to MetaMask" entry
// in NOISE_MESSAGES — never reachable from our app.
const WALLET_EXTENSION_CSP_HOSTS = [
  "wallet.binance.com",
  "safesearchinc.com",
];

// Social-share browser extensions ("Share to Facebook", FB Container, etc.)
// trip frame-src / form-action by opening Facebook share dialogs from any
// page the user visits. We don't render Facebook share buttons; adding
// www.facebook.com to frame-src/form-action would only widen the policy
// for extension-driven noise.
const SOCIAL_SHARE_NOISE_HOSTS = [
  "www.facebook.com",
];

// Google Ads can trigger connect-src reports for DoubleClick endpoints even
// though the HTTPS origin is already in the enforced policy. Keep this out of
// GlitchReplay's error feed rather than broadening CSP for tracker noise.
const GOOGLE_ADS_CSP_NOISE_HOSTS = [
  "static.doubleclick.net",
  "www.google.com",
];

// Google Ads conversion pixels follow the user's locale — same campaign
// fires at www.google.com.gt, .co.uk, .ca, .co.in, .com.eg, .com.br, etc.
// The site only enumerates google.com / *.gstatic.com in img-src; the
// per-ccTLD variants are blocked but represent the same tracking pixel,
// not broken functionality. Allowing every Google ccTLD would weaken the
// policy for no rendering benefit. Filter on suffix instead.
const GOOGLE_CCTLD_PIXEL_PATTERN = /^www\.google\.[a-z.]+$/i;

// Status-aware verification for first-party Cloudflare Images
// (imagedelivery.net) resource_error reports. networkProbeIntegration's
// per-URL probe uses mode:'no-cors', which resolves on ANY response —
// including a 404 from a deleted/renamed image id — so its
// `gr.probe=reachable` survivors still include real breakage AND pure
// client-side noise (a non-rendering JS agent or transient blip whose
// <img>/<link> fired `error` while the asset is actually fine; e.g. the
// clean-UA Firefox/Linux client in gr_vsm79dn/gr_wvbvag3/gr_pywqzvz).
// imagedelivery.net sends `access-control-allow-origin: *`, so a cors
// fetch CAN read the real status: returns true only on a genuine HTTP 200,
// letting beforeSend drop the noise while a 404 (or an unverifiable fetch
// failure) is kept so real broken images still surface. Memoized per URL.
const imageOkMemo = new Map<string, Promise<boolean>>();
function verifyImageOk(url: string): Promise<boolean> {
  const cached = imageOkMemo.get(url);
  if (cached) return cached;
  if (typeof fetch !== "function") {
    const p = Promise.resolve(false);
    imageOkMemo.set(url, p);
    return p;
  }
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 4_000) : null;
  const p = fetch(url, {
    mode: "cors",
    cache: "no-store",
    credentials: "omit",
    signal: controller?.signal,
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
  imageOkMemo.set(url, p);
  return p;
}

Sentry.init({
  dsn: DSN,
  release:
    process.env.NEXT_PUBLIC_RELEASE ||
    process.env.NEXT_PUBLIC_COMMIT_SHA ||
    "dev",
  // Always-on session replay. Buffer mode (`replaysSessionSampleRate: 0`
  // + `replaysOnErrorSampleRate: 1.0`) silently drops first-frame errors:
  // the rrweb full snapshot is queued asynchronously, so when a React
  // hydration error (e.g. #418) fires during the first commit the buffer
  // is empty and the on-error flush sends nothing. Session mode starts
  // the recorder synchronously at init and uploads segments continuously,
  // so even errors thrown before the first snapshot land still produce a
  // viewable replay (post-error frames at minimum). Counter-evidence:
  // gr_xkggqbf, financialtoolset.com, 2026-04-27.
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
  denyUrls: NOISE_DENY_URLS,
  integrations: [
    Sentry.replayIntegration({
      ...PRIVACY,
      // rrweb's dead/slow-click heuristic flags a click as "dead" when no
      // DOM mutation follows within slowClickTimeout. On article prose,
      // tables, code blocks and headings a click is text selection, never an
      // action — pure false positives that have made up ~half of past
      // GlitchReplay batches. Ignore those containers; real interactive
      // elements still get dead-click detection (rage-click is unaffected).
      //
      // Form fields (input/select/textarea) self-focus on click without
      // mutating the DOM, so rrweb marks the click "dead" even though it
      // succeeded. Same for the site logo (an <img> nested in <Link>) —
      // navigation happens after replay capture, looking like a dead click.
      // h1/h2 headings are user-selected for copy/share; never an action.
      // Disabled buttons (`[disabled]`) are intentionally inert. The hero
      // wrapper `.hero-entrance` is a Framer-Motion animation container
      // where clicks land between the heading and the CTA.
      slowClickIgnoreSelectors: [
        ".prose",
        ".prose *",
        '[aria-label="Table"]',
        '[aria-label="Table"] *',
        ".heading-with-anchor",
        "pre",
        "pre *",
        "table",
        "table *",
        "input",
        "select",
        "textarea",
        'img[alt="Inventive HQ"]',
        "h1",
        "h2",
        "h3",
        "[disabled]",
        ".hero-entrance",
        ".hero-entrance *",
      ],
    }),
    axeIntegration({
      runOn: ["development", "staging", "production"],
      // RUM-style sampling: 1% of prod page loads run a single axe scan
      // via requestIdleCallback. Was 5% pre-#80 — dropped to 1% as a
      // safer trade-off against the INP regression that prompted #80,
      // while keeping prod coverage so new a11y regressions surface.
      // Fingerprint dedup means full rule coverage still accumulates
      // across visitors within a day at this rate.
      productionSampleRate: 0.01,
      wcagLevel: "AA",
      minImpact: "moderate",
    }),
    // Drops imagedelivery.net "Failed to load <img>" reports caused by
    // ad-blockers / privacy extensions / DNS filters at the network
    // layer. Verifies real failures via per-URL probe so genuine CDN
    // outages still surface (tagged gr.probe=reachable).
    // img.youtube.com: blog posts embed YouTube thumbnails as
    // maxresdefault.jpg with hqdefault.jpg fallback; videos that lack a
    // max-res thumb 404 by design (Counter-evidence: gr_8mz5sm9 for
    // 6xgeNhmm6Ag, HlfRTCcgPR8, uhiY3JSwFfw — all small channels).
    networkProbeIntegration({
      verifyResourceErrors: [/imagedelivery\.net/i, /img\.youtube\.com/i],
    }),
    // Captures fetch/XHR 5xx responses. The browser auto-logs failed
    // requests to the console, but `fetch()` doesn't reject on HTTP error
    // status, so no exception or unhandled rejection fires — default
    // integrations miss them. Defaults: status [[500, 599]], targets /.*/.
    Sentry.httpClientIntegration(),
    // Captures Content-Security-Policy violations via the browser's
    // `securitypolicyviolation` event. The CSP (next.config.mjs +
    // middleware.ts) carries no report-uri/report-to directive and Sentry
    // has no native CSP instrumentation, so without this a blocked
    // script/style/connect is invisible beyond the user's own console.
    // Extension-injected violations are filtered by URL scheme; wallet
    // extensions that inject into the page's main world need a host-based
    // filter (beforeCapture) on top. Local integration for now — see
    // lib/glitchreplay-csp.ts ("extract later").
    cspIntegration({
      beforeCapture: (violation) => {
        let host = "";
        try {
          host = new URL(violation.blockedURI).host;
        } catch {
          return true;
        }
        if (
          WALLET_EXTENSION_CSP_HOSTS.some(
            (h) => host === h || host.endsWith("." + h),
          )
        ) {
          return false;
        }
        if (SOCIAL_SHARE_NOISE_HOSTS.includes(host)) return false;
        if (GOOGLE_ADS_CSP_NOISE_HOSTS.includes(host)) return false;
        if (host !== "www.google.com" && GOOGLE_CCTLD_PIXEL_PATTERN.test(host)) {
          return false;
        }
        return true;
      },
    }),
  ],
  beforeSend(event, hint) {
    const message =
      event.message || (hint?.originalException as Error | undefined)?.message || "";
    if (NOISE_MESSAGES.some((n) => message.includes(n))) return null;
    // Page proxies that rewrite the DOM (Google Translate's *.translate.goog,
    // Yandex Translate, generic *.translate.* mirrors) trigger recursion in
    // rrweb / IntersectionObserver chains we don't control. The frame URLs
    // point at the proxy host, so denyUrls (which matches script source) misses
    // them — filter on the event request URL here instead.
    const requestUrl = event.request?.url || "";
    if (/\.translate\.goog/i.test(requestUrl)) return null;
    // axe a11y events whose target / html lives inside a browser extension's
    // injected shadow root or overlay: Adobe Acrobat reader extension's
    // "Get summaries..." floating button (`#aiFabShadowRoot,.acrobat-button`),
    // OpenAI ChatGPT-sidebar extension's draggable handle (`chatgpt-sidebar`).
    // Neither lives in our DOM tree, but axe-core walks document.* including
    // injected nodes, so they surface as link-name / button-name / nested-
    // interactive failures. Counter-evidence: gr_zd5jcrk (Acrobat, blog),
    // gr_2gjne3p (ChatGPT sidebar, firewall-rule-simulator).
    const a11yEvent = event.contexts?.a11y as
      | { target?: string[]; html?: string }
      | undefined;
    if (a11yEvent) {
      const a11yTarget = Array.isArray(a11yEvent.target)
        ? a11yEvent.target.join(" ")
        : "";
      const a11yHtml = a11yEvent.html || "";
      if (
        /chatgpt-sidebar|aiFabShadowRoot|acrobat-button|grammarly-|lastpass-|honey-extension/i.test(
          a11yTarget,
        )
      ) {
        return null;
      }
      // In-page Google Translate wraps every text node in
      // <font dir="auto" style="vertical-align: inherit;"> and sometimes
      // nests those wrappers. Axe then reports violations against the
      // injected <font> nodes instead of our authored DOM. The HTML / target
      // fields carry that fingerprint. Same root cause as the #418 hydration
      // filter above. Counter-evidence: gr_p4mvf8z (url-expander, blog
      // comments), gr_p7zr6n1 (color-contrast, nested Translate font).
      if (
        /<font[^>]+dir="auto"[^>]*>\s*<\/font>/i.test(a11yHtml) ||
        (/> font(?::[^\s>]+)? > font/i.test(a11yTarget) && /<font\b/i.test(a11yHtml))
      ) {
        return null;
      }
    }
    // Cloudflare Rocket Loader (cdn-cgi/scripts/.../rocket-loader.min.js)
    // defers and re-injects every <script> on the page; when it interacts
    // with React it crashes mid-commit with "Cannot read properties of null
    // (reading 'offsetHeight' / 'tagName')" or surfaces AbortError from its
    // own fetch wrappers. The first frame is always inside the rocket-loader
    // chunk — that's not code we ship. The proper fix is to disable Rocket
    // Loader in the Cloudflare dashboard; the filter keeps the report queue
    // clean until that lands. Counter-evidence: gr_8tdaer4 (hash-lookup,
    // offsetHeight), gr_4qpqkmy (ascii-art-generator, AbortError).
    const firstFrame =
      event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
    if (
      firstFrame?.filename &&
      /\/cdn-cgi\/scripts\/[^/]+\/cloudflare-static\/rocket-loader/i.test(
        firstFrame.filename,
      )
    ) {
      return null;
    }
    // Web Worker importScripts NetworkError from a blob: source — browser
    // extensions (ad blockers, accessibility helpers) sometimes spawn a
    // Worker from `URL.createObjectURL(new Blob(...))` and then race the
    // page lifecycle: navigation revokes the parent blob before the worker
    // imports its sibling chunk. The error's only frame is a blob: URL we
    // don't own. Counter-evidence: gr_7nc2bxq.
    if (
      firstFrame?.filename?.startsWith("blob:") &&
      /importScripts.*WorkerGlobalScope/i.test(
        event.exception?.values?.[0]?.value || "",
      )
    ) {
      return null;
    }
    // Browser-extension content scripts wrap window.addEventListener to
    // observe events (password managers, ad-blockers, accessibility tools).
    // Their hook function `addEL_hook` shows up as an anonymous frame with
    // no filename when the wrapped React handler later throws. We can't fix
    // a third-party hook from our code; same class as the rocket-loader
    // filter. Counter-evidence: gr_jq3xcmw (spf-10-dns-lookup-limit blog,
    // tagName null with addEL_hook in the stack).
    const stackFrames =
      event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    if (stackFrames.some((f) => /addEL_hook/i.test(f.function || ""))) {
      return null;
    }
    // Resource error for a <script> URL that lives in the NOISE_DENY_URLS
    // list. denyUrls already drops errors WHOSE STACK matches those scripts,
    // but a `resource_error` event has no stack — the URL is in
    // exception.values[0].value as "Failed to load <script>: <URL>". Match
    // on that string so script-load failures from the same allow-list are
    // filtered consistently. Counter-evidence: gr_x8q2hru
    // (domain-spoofing-detector, jsdelivr jspdf load fail injected by an
    // extension).
    const resourceErrorValue = event.exception?.values?.[0]?.value || "";
    if (
      event.exception?.values?.[0]?.type === "resource_error" &&
      /Failed to load <(script|link)>:/i.test(resourceErrorValue) &&
      NOISE_DENY_URLS.some((re) => re.test(resourceErrorValue))
    ) {
      return null;
    }
    // HTTP Client errors (fetch/XHR 5xx) from noise-listed URLs.
    // denyUrls only matches stack frames; httpClientIntegration sets
    // event.request.url to the failing URL, which denyUrls doesn't check.
    const httpClientErrorValue =
      message || event.exception?.values?.[0]?.value || "";
    if (
      /^HTTP Client Error with status code:/i.test(httpClientErrorValue) &&
      (NOISE_DENY_URLS.some((re) => re.test(event.request?.url || "")) ||
        new RegExp(`^https://${GLITCHREPLAY_HOST.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/`).test(
          event.request?.url || "",
        ))
    ) {
      return null;
    }
    // React reconciler crash from external DOM mutation: in-page browser
    // translation (Chrome iOS / Safari Translate) or an extension relocates
    // a node, then commit-phase insertBefore/removeChild/appendChild throws
    // NotFoundError. Not our bug — and the page keeps its real URL, so the
    // *.translate.goog check above can't catch it. In production the React
    // commit functions are minified (uf/uh/uy…), so the original `f.function`
    // regex never matched a real build — key off the error message, which the
    // DOM API always spells out in full ("Failed to execute 'removeChild' on
    // 'Node': The node to be removed is not a child of this node.").
    // Counter-evidence: gr_cyxmqrr (CT-lookup, CriOS), gr_yks354y (xor-cipher).
    const domMutationValue = event.exception?.values?.[0]?.value || message || "";
    if (
      (event.exception?.values?.[0]?.type === "NotFoundError" ||
        /Failed to execute '(insertBefore|removeChild|appendChild|replaceChild)'/i.test(
          domMutationValue,
        )) &&
      (/Failed to execute '(insertBefore|removeChild|appendChild|replaceChild)'/i.test(
        domMutationValue,
      ) ||
        /not a child of this node/i.test(domMutationValue) ||
        /The object can ?not be found here/i.test(domMutationValue) ||
        (event.exception?.values?.[0]?.stacktrace?.frames ?? []).some((f) =>
          /insertBefore|removeChild|appendChild/i.test(f.function || ""),
        ))
    ) {
      return null;
    }
    // React hydration mismatches (#418 HTML/text, #423/#425 server text)
    // with a stack that lives entirely inside React's compiled chunk —
    // no app frames at all. Our pages are static / RSC-rendered with no
    // dynamic client-side text that could legitimately diverge from the
    // server output, so when we see one of these it's almost always
    // in-page browser translation (Chrome iOS / Safari Translate /
    // Android Chrome's auto-translate) rewriting text nodes between SSR
    // and the first commit. Same class as the insertBefore/removeChild
    // filter above; #418 just lands earlier in the commit phase before
    // any DOM-API call throws. Counter-evidence: gr_yu5snvm
    // (ascii-art-generator, Android Chrome auto-translate),
    // gr_g8nqkrj (hex-editor, iOS Safari).
    if (/Minified React error #(418|423|425)/i.test(domMutationValue)) {
      const stackFrames =
        event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const hasAppFrame = stackFrames.some(
        (f) =>
          !!f.filename &&
          !/_next\/static\/chunks\//.test(f.filename) &&
          !/\.translate\./.test(f.filename),
      );
      if (!hasAppFrame) return null;
    }
    // Webpack runtime "Cannot read properties of undefined (reading
    // 'call')" with the first frame inside webpack-*.js — this fires
    // when a stale tab navigates client-side and asks the runtime for a
    // module ID that doesn't exist in the freshly-loaded chunks. No app
    // code is at fault; the in-app frame is webpack's `__webpack_require__`
    // which we don't own. A hard reload would resolve it (handled in
    // app/error.tsx for caught cases), but most fire from background
    // navigation Promises and never reach the boundary. Counter-evidence:
    // gr_jws53hc (knowledge-base, 127 occurrences in 36 h — single
    // fingerprint, no app frames).
    if (
      event.exception?.values?.[0]?.type === "TypeError" &&
      /Cannot read properties of undefined \(reading 'call'\)|undefined is not an object \(evaluating 'e\[n\]\.call'\)|can't access property "call", e\[n\] is undefined/i.test(
        domMutationValue,
      )
    ) {
      const firstFrame =
        event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
      if (
        firstFrame?.filename &&
        /\/_next\/static\/chunks\/webpack-/.test(firstFrame.filename)
      ) {
        return null;
      }
    }
    // Loading-chunk failures during client-side navigation: same stale-
    // tab class, surfaces as "Loading chunk N failed" with the chunk
    // URL in the message. app/error.tsx detects this and shows a
    // recovery UI, but Promise-internal failures never reach the
    // boundary. Counter-evidence: gr_8r2zqx5.
    if (/^Loading chunk \d+ failed\./i.test(domMutationValue)) {
      return null;
    }
    // SEO / archive crawlers that execute JS (SEBot, AhrefsBot, SemrushBot,
    // etc.) sometimes mutate the DOM out from under React's commit phase,
    // producing reconciler errors like "Cannot read properties of null
    // (reading 'firstChild')" from inside the React chunk with no app frames.
    // These aren't real user-facing bugs — same class as the translate.goog
    // filter above. Counter-evidence: gr_m4djqk4 (SEBot-WA, world-clock).
    // `notebooklm` covers Google-NotebookLM, an AI page-fetcher whose UA
    // carries no "bot"/"crawler" token. It doesn't load images, so it emits
    // bogus imagedelivery.net resource_error reports (gr_3w9kx94 et al.) —
    // verified the images return HTTP 200 in a real browser. `adsense` covers
    // GoogleAdSenseInfeed, Google's AdSense in-feed page renderer (UA ends
    // "…Galaxy Nexus…GoogleAdSenseInfeed"), which emits transient
    // "Connection closed" errors (gr_3y8tv2y).
    const ua =
      (event.request?.headers as Record<string, string> | undefined)?.["User-Agent"] || "";
    if (/bot|crawler|spider|scraper|headlesschrome|notebooklm|adsense/i.test(ua)) return null;
    // Next.js <Link> prefetches hashed chunk JS / CSS / fonts for routes
    // the user might navigate to. Those prefetches fail for reasons we
    // can't act on: a stale tab pointing at an asset hash that's been
    // replaced by a newer deploy (404), aggressive privacy extensions
    // blocking /_next/, or the browser aborting an in-flight prefetch
    // when the user navigates away. None block real navigation —
    // Next.js re-fetches on click. Real failures during actual
    // navigation surface as separate JS errors with stacks.
    // Counter-evidence: gr_rvr77q8/4q3dr46/8yz926e/xbnd2v6/wc9nmwf/j4rxd9g.
    const exValue = event.exception?.values?.[0]?.value || "";
    if (
      event.exception?.values?.[0]?.type === "resource_error" &&
      /_next\/static\/(chunks|css|media)\//.test(exValue)
    ) {
      return null;
    }
    // Google Translate widget assets failing to load — pure third-party
    // noise from in-page translation, never an actionable CDN failure.
    // Counter-evidence: gr_y7ptjs2 (gstatic translate_http CSS, CT-lookup).
    if (
      event.exception?.values?.[0]?.type === "resource_error" &&
      /translate_http|translate\.goog|\/_\/translate/i.test(exValue + " " + requestUrl)
    ) {
      return null;
    }
    // First-party Cloudflare Images resource_error that survived
    // networkProbeIntegration's no-cors probe (gr.probe=reachable). Re-verify
    // the real HTTP status via cors fetch: a true 200 means the element
    // failed to render but the asset is healthy (client-side noise) — drop
    // it; a 404/other status or an unverifiable fetch is kept so a genuinely
    // broken/renamed image still surfaces. See verifyImageOk above.
    if (
      event.exception?.values?.[0]?.type === "resource_error" &&
      /imagedelivery\.net/i.test(exValue)
    ) {
      const imgUrl = exValue.match(/https?:\/\/[^\s>]+/)?.[0];
      if (imgUrl) {
        return verifyImageOk(imgUrl).then((ok) => (ok ? null : event));
      }
    }
    // Stack-less rejections from embedded widgets / browser quirks: drop
    // the message when there's no usable stack so legitimate fetch
    // failures with a real call site still surface.
    const hasUsableStack = (event.exception?.values ?? []).some(
      (value) => (value.stacktrace?.frames?.length ?? 0) > 0,
    );
    if (!hasUsableStack) {
      // Sentry's synthetic "Non-Error promise rejection captured with
      // value: …" text lands in exception.values[].value, not event.message
      // (event.message is undefined for exception events) — fall back to it
      // so the patterns above can actually match. Without this, third-party
      // rejections like Outlook SafeLink's "Object Not Found Matching Id"
      // slip through. Counter-evidence: gr_8d3ypx8.
      const rejectionText =
        message || event.exception?.values?.[0]?.value || "";
      const matches = NOISE_REJECTION_MESSAGES_NO_STACK.some((p) =>
        typeof p === "string" ? rejectionText === p : p.test(rejectionText)
      );
      if (matches) return null;
    }
    return event;
  },
});

// Dashboard-controlled session sampling. Static init above starts session
// replay at 1.0 to guarantee first-frame error capture. This side-channel
// lets the dashboard *down-sample* (or stop) the live recorder when a
// project owner wants to dial back replay volume without redeploying — if
// the dashboard returns `enabled: false` or a sub-1.0 sample rate that
// loses the dice roll, we stop the in-progress recording.
type ReplayCfg = {
  replay?: {
    enabled?: boolean;
    sessionSampleRate?: number;
    onErrorSampleRate?: number;
    maskAllText?: boolean;
    maskAllInputs?: boolean;
    blockAllMedia?: boolean;
  };
};

// Session-attempt beacon. Counts every SDK init for the dashboard's
// errors/session denominator — the /config fetch below decides whether
// to *persist* this recording, but every attempt should count. sendBeacon
// survives pagehide so quick-bounce visitors are still tallied, and never
// delays unload.
try {
  navigator.sendBeacon(
    `https://${GLITCHREPLAY_HOST}/api/${PROJECT_ID}/session-attempt?sentry_key=${PUBLIC_KEY}`,
  );
} catch {}

fetch(
  `https://${GLITCHREPLAY_HOST}/api/${PROJECT_ID}/config?sentry_key=${PUBLIC_KEY}`,
  { cache: "no-store" },
)
  .then(async (r): Promise<ReplayCfg | null> => (r.ok ? ((await r.json()) as ReplayCfg) : null))
  .then((cfg) => {
    // Cache the dashboard's current privacy flags for next page load.
    if (
      cfg?.replay &&
      typeof cfg.replay.maskAllText === "boolean" &&
      typeof cfg.replay.maskAllInputs === "boolean" &&
      typeof cfg.replay.blockAllMedia === "boolean"
    ) {
      try {
        localStorage.setItem(
          PRIVACY_CACHE_KEY,
          JSON.stringify({
            maskAllText: cfg.replay.maskAllText,
            maskAllInputs: cfg.replay.maskAllInputs,
            blockAllMedia: cfg.replay.blockAllMedia,
          }),
        );
      } catch {}
    }

    const replay = Sentry.getReplay();
    if (!replay) return;
    if (!cfg?.replay?.enabled) {
      replay.stop();
      return;
    }
    const sessionRate = Number(cfg.replay.sessionSampleRate ?? 1);
    if (sessionRate < 1 && Math.random() >= sessionRate) {
      replay.stop();
    }
  })
  .catch(() => {
    // Network/CORS/whatever — leave the static 1.0 session recorder running.
  });
